import { useRef, useState, type FormEvent } from 'react';
import type { EnvValueType } from '@hangar/shared';
import { toast } from 'sonner';
import { useEnvVarMutations } from '@/api/hooks';
import type { EnvVar } from '@/api/schemas';
import { serverErrorsOf } from '@/lib/formErrors';
import { showErrorToast } from '@/lib/toasts';
import type { ValueState } from './ValueEditor';
import { isValueEmpty, newVersionBody, newVersionToastDescription, warningsToast } from './versionModel';

/** What the user typed: the value (or file), its type and the audit note. */
function useVersionDraft(initialType: EnvValueType) {
  const [value, setValue] = useState<ValueState>({ value: '' });
  const [valueType, setValueType] = useState<EnvValueType>(initialType);
  const [note, setNote] = useState('');
  return { value, setValue, valueType, setValueType, note, setNote };
}

/** State and submit of the "new version" form; `expectedCurrentVersion` guards against concurrent writers. */
export function useNewVersionForm(envVar: EnvVar, onDone: () => void) {
  const { newVersion } = useEnvVarMutations();
  const { value, setValue, valueType, setValueType, note, setNote } = useVersionDraft(envVar.valueType);
  const [submitted, setSubmitted] = useState(false);
  const [problem, setProblem] = useState<unknown>(null);
  const idempotencyKey = useRef(crypto.randomUUID());
  const current = envVar.currentVersion?.version;

  const save = async () => {
    const result = await newVersion.mutateAsync({ id: envVar.id, body: newVersionBody({ value, note, current }), idempotencyKey: idempotencyKey.current });
    idempotencyKey.current = crypto.randomUUID();
    toast.success(`${envVar.key} v${result.version.version}`, { description: newVersionToastDescription(result.approval, result.version.valueFingerprint) });
    const warnings = warningsToast(result.warnings);
    if (warnings) toast.warning(warnings.title, { description: warnings.description });
    onDone();
  };
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setProblem(null);
    if (isValueEmpty(value)) return;
    try {
      await save();
    } catch (err) {
      const mapped = serverErrorsOf(err);
      setProblem(err);
      if (mapped.toast) showErrorToast(mapped.toast);
    }
  };
  return { value, setValue, valueType, setValueType, note, setNote, submitted, problem, current, onSubmit, isPending: newVersion.isPending };
}
